import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { ASSETS, NAP } from "../lib/data";
import { TID } from "../lib/testIds";

const NAV_ITEMS = [
  { to: "/", label: "Home", tid: TID.navHome },
  { to: "/about", label: "About", tid: TID.navAbout },
  { to: "/services", label: "Services", tid: TID.navServices },
  { to: "https://blog.actfastrepair.com/", label: "Blog", tid: TID.navBlog, external: true },
  { to: "/contact", label: "Contact", tid: TID.navContact },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-300 ${
        scrolled ? "shadow-md border-b border-neutral-200" : "border-b border-neutral-200/60"
      }`}
    >
      <nav className="w-full max-w-7xl mx-auto px-4 lg:px-8 h-20 lg:h-28 flex justify-between items-center gap-3 lg:gap-6 overflow-hidden">
        {/* Logo block — anchored to the left, with a constrained painted width on mobile so it can never collide with the hamburger on the right */}
        <Link
          to="/"
          className="flex items-center shrink-0 w-[140px] sm:w-[180px] lg:w-[30%] lg:max-w-[440px] overflow-hidden"
          data-testid={TID.navLogo}
          aria-label="Act Fast Appliance Repair"
        >
          <div
            className="h-12 sm:h-14 lg:h-24 w-full bg-no-repeat bg-left bg-contain"
            style={{
              backgroundImage: `url("${ASSETS.logo}")`,
              transform: "scale(1.25)",
              transformOrigin: "left center",
            }}
            role="img"
            aria-label="Act Fast Appliance Repair"
          />
        </Link>

        <ul className="hidden lg:flex items-center gap-8 xl:gap-10">
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              {item.external ? (
                <a
                  href={item.to}
                  data-testid={item.tid}
                  className="af-link-underline text-sm uppercase tracking-[0.15em] font-bold transition-colors text-black hover:text-[#DC2626]"
                >
                  {item.label}
                </a>
              ) : (
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={item.tid}
                  className={({ isActive }) =>
                    `af-link-underline text-sm uppercase tracking-[0.15em] font-bold transition-colors ${
                      isActive ? "text-[#DC2626]" : "text-black hover:text-[#DC2626]"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )}
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={NAP.phoneTel}
            data-testid={TID.navCallBtn}
            className="af-btn-primary text-sm"
          >
            <Phone size={16} />
            {NAP.phoneDisplay}
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden shrink-0 text-black p-2.5 border border-neutral-300 hover:border-[#DC2626] bg-white"
          data-testid={TID.navMobileToggle}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-white border-t border-neutral-200">
          <ul className="px-5 py-4 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                {item.external ? (
                  <a
                    href={item.to}
                    data-testid={`mobile-${item.tid}`}
                    className="block px-3 py-3 text-black hover:text-[#DC2626] hover:bg-neutral-100 uppercase text-sm tracking-wider font-bold"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    to={item.to}
                    data-testid={`mobile-${item.tid}`}
                    className="block px-3 py-3 text-black hover:text-[#DC2626] hover:bg-neutral-100 uppercase text-sm tracking-wider font-bold"
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
            <li className="mt-3">
              <a href={NAP.phoneTel} className="af-btn-primary w-full justify-center text-sm">
                <Phone size={16} /> {NAP.phoneDisplay}
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
